import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

async function j(path: string, init?: RequestInit) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg = (await res.json()).detail || msg; } catch { /* noop */ }
    throw new Error(String(msg));
  }
  return res.json();
}

type Options = { country: string[]; supplyTypes: string[]; currency: string[] };
type Supplier = {
  id: string; name: string; phone?: string; whatsapp?: string; email?: string; website?: string;
  country?: string; supplyTypes: string[]; currency?: string; paymentTerms?: string;
  moq?: string; leadTimeDays?: number | null; rating?: number | null; status: string; notes?: string;
  ifra: { fileId?: string; fileName?: string; expiresAt?: string; status: string };
  materialsCount?: number;
};
type Mat = { id: string; name: string; stockOnHand: number | null; unit?: string };
type PO = {
  id: string; poNumber: string; supplierId: string; supplierName: string; currency: string;
  status: string; lines: { rmId: string; materialName: string; qtyG: number; unitCost: number | null; receivedG: number }[];
  notes?: string;
};

const EMPTY_FORM = {
  name: "", phone: "", whatsapp: "", email: "", website: "", country: "", supplyTypes: [] as string[],
  currency: "EGP", paymentTerms: "", moq: "", leadTimeDays: "", rating: "", status: "active", notes: "",
};

const STATUS_AR: Record<string, string> = {
  active: "نشِط", paused: "موقوف", archived: "مؤرشف",
  draft: "مسودّة", sent: "مُرسَل للمورّد", partially_received: "استلام جزئي",
  received: "مُستلَم بالكامل", cancelled: "ملغي",
};

function ifraTone(status: string): { bg: string; fg: string } {
  if (status === "سارية") return { bg: "rgba(46,160,90,0.15)", fg: "#2ea05a" };
  if (status === "تنتهي قريبًا") return { bg: "rgba(230,160,40,0.15)", fg: "#e0a028" };
  if (status === "منتهية") return { bg: "rgba(220,70,70,0.15)", fg: "#e05656" };
  return { bg: "rgba(150,150,150,0.15)", fg: "#9aa0aa" };
}

export default function SuppliersPage() {
  const [view, setView] = useState<"suppliers" | "pos">("suppliers");
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [options, setOptions] = useState<Options>({ country: [], supplyTypes: [], currency: [] });
  const [alerts, setAlerts] = useState<{ supplierId: string; name: string; status: string; expiresAt?: string }[]>([]);
  const [allMats, setAllMats] = useState<Mat[]>([]);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [ifraFile, setIfraFile] = useState<File | null>(null);
  const [ifraExpiry, setIfraExpiry] = useState("");
  const [linkedMats, setLinkedMats] = useState<Mat[]>([]);
  const [matToLink, setMatToLink] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, o, a] = await Promise.all([
      j(`/suppliers${q ? `?q=${encodeURIComponent(q)}` : ""}`),
      j(`/suppliers/options`),
      j(`/suppliers/ifra/alerts`),
    ]);
    setSuppliers(s); setOptions(o); setAlerts(a);
  };
  useEffect(() => { load().catch((e) => toast.error(String(e.message))); }, [q]);
  useEffect(() => {
    j(`/blend/inventory`).then((rows) => setAllMats(rows)).catch(() => setAllMats([]));
  }, []);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY_FORM }); setIfraFile(null); setIfraExpiry(""); setLinkedMats([]); setFormOpen(true); };
  const openEdit = async (s: Supplier) => {
    setEditId(s.id);
    setForm({
      name: s.name || "", phone: s.phone || "", whatsapp: s.whatsapp || "", email: s.email || "",
      website: s.website || "", country: s.country || "", supplyTypes: s.supplyTypes || [],
      currency: s.currency || "EGP", paymentTerms: s.paymentTerms || "", moq: s.moq || "",
      leadTimeDays: s.leadTimeDays != null ? String(s.leadTimeDays) : "", rating: s.rating != null ? String(s.rating) : "",
      status: s.status || "active", notes: s.notes || "",
    });
    setIfraFile(null); setIfraExpiry(s.ifra?.expiresAt || "");
    setFormOpen(true);
    try { setLinkedMats(await j(`/suppliers/${s.id}/materials`)); } catch { setLinkedMats([]); }
  };

  const addOption = async (key: keyof Options) => {
    const value = window.prompt("أدخل الخيار الجديد:");
    if (!value) return;
    const o = await j(`/suppliers/options`, { method: "POST", body: JSON.stringify({ key, value }) });
    setOptions(o);
    toast.success("اتضاف للخيارات");
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("اسم المورّد مطلوب"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        leadTimeDays: form.leadTimeDays ? Number(form.leadTimeDays) : null,
        rating: form.rating ? Number(form.rating) : null,
      };
      const saved = editId
        ? await j(`/suppliers/${editId}`, { method: "PATCH", body: JSON.stringify(payload) })
        : await j(`/suppliers`, { method: "POST", body: JSON.stringify(payload) });
      // IFRA upload if provided
      if (ifraFile || ifraExpiry !== (saved.ifra?.expiresAt || "")) {
        const body: Record<string, unknown> = { expiresAt: ifraExpiry || null };
        if (ifraFile) {
          const b64 = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(String(r.result).split(",")[1]);
            r.onerror = reject;
            r.readAsDataURL(ifraFile);
          });
          body.dataBase64 = b64; body.fileName = ifraFile.name; body.mime = ifraFile.type || "application/pdf";
        }
        await j(`/suppliers/${saved.id}/ifra`, { method: "POST", body: JSON.stringify(body) });
      }
      toast.success(editId ? "تم حفظ التعديلات" : "تم إضافة المورّد");
      setFormOpen(false);
      await load();
    } catch (e: any) { toast.error(String(e.message)); }
    finally { setBusy(false); }
  };

  const remove = async (sid: string) => {
    if (!window.confirm("متأكد من حذف المورّد؟ الخامات المرتبطة هتتفك من غير حذفها.")) return;
    try {
      await j(`/suppliers/${sid}`, { method: "DELETE" });
      toast.success("تم الحذف");
      setFormOpen(false);
      await load();
    } catch (e: any) { toast.error(String(e.message)); }
  };

  const linkMaterial = async () => {
    if (!editId || !matToLink) return;
    await j(`/suppliers/materials/assign`, { method: "POST", body: JSON.stringify({ rmId: matToLink, supplierId: editId }) });
    setLinkedMats(await j(`/suppliers/${editId}/materials`));
    setMatToLink("");
    toast.success("اتربطت بالمورّد");
  };
  const unlinkMaterial = async (rmId: string) => {
    await j(`/suppliers/materials/assign`, { method: "POST", body: JSON.stringify({ rmId, supplierId: null }) });
    if (editId) setLinkedMats(await j(`/suppliers/${editId}/materials`));
  };

  const critical = alerts.filter((a) => a.status !== "غير مرفوعة");
  const missingCount = alerts.filter((a) => a.status === "غير مرفوعة").length;

  return (
    <div className="space-y-4" data-testid="suppliers-page">
      <div className="flex gap-2 items-center flex-wrap">
        <button className={`bl-tab ${view === "suppliers" ? "active" : ""}`} onClick={() => setView("suppliers")} data-testid="suppliers-view-tab">🚚 الموردين</button>
        <button className={`bl-tab ${view === "pos" ? "active" : ""}`} onClick={() => setView("pos")} data-testid="pos-view-tab">🧾 أوامر الشراء والاستلام</button>
      </div>

      {critical.length > 0 && (
        <div className="bl-card" style={{ borderColor: "rgba(224,86,86,0.5)" }} data-testid="ifra-alerts-banner">
          <div className="bl-eyebrow" style={{ color: "#e05656" }}>⚠️ تنبيه شهادات IFRA</div>
          {critical.map((a) => (
            <div key={a.supplierId} className="bl-sub mt-1" data-testid={`ifra-alert-${a.supplierId}`}>
              {a.name}: <b style={{ color: ifraTone(a.status).fg }}>{a.status}</b>{a.expiresAt ? ` · تنتهي ${a.expiresAt}` : ""}
            </div>
          ))}
        </div>
      )}

      {view === "suppliers" && (
        <>
          <div className="flex gap-2 items-center">
            <input className="bl-input flex-1" placeholder="ابحث باسم المورّد…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="supplier-search-input" />
            <button className="bl-btn" onClick={openCreate} data-testid="supplier-add-btn">+ إضافة مورّد</button>
          </div>
          {missingCount > 0 && <div className="bl-sub" data-testid="ifra-missing-count">🗂 {missingCount} مورّد من غير شهادة IFRA مرفوعة</div>}

          <div className="bl-card">
            <table className="bl-table" data-testid="suppliers-table">
              <thead><tr><th>المورّد</th><th>الدولة</th><th>نوع التوريد</th><th>العملة</th><th>التقييم</th><th>خامات</th><th>IFRA</th><th>الحالة</th></tr></thead>
              <tbody>
                {suppliers.map((s) => {
                  const tone = ifraTone(s.ifra.status);
                  return (
                    <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => openEdit(s)} data-testid={`supplier-row-${s.id}`}>
                      <td><b>{s.name}</b>{s.phone ? <div className="bl-sub" style={{ fontSize: 10 }}>{s.phone}</div> : null}</td>
                      <td>{s.country || "—"}</td>
                      <td style={{ maxWidth: 220 }}>{(s.supplyTypes || []).join("، ") || "—"}</td>
                      <td>{s.currency || "—"}</td>
                      <td>{s.rating != null ? `${s.rating}/5` : "—"}</td>
                      <td>{s.materialsCount || 0}</td>
                      <td><span className="bl-pill" style={{ background: tone.bg, color: tone.fg }} data-testid={`supplier-ifra-${s.id}`}>{s.ifra.status}</span></td>
                      <td>{STATUS_AR[s.status] || s.status}</td>
                    </tr>
                  );
                })}
                {suppliers.length === 0 && <tr><td colSpan={8} className="bl-sub text-center py-6" data-testid="suppliers-empty">لا يوجد موردين — ابدأ بإضافة أول مورّد</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === "pos" && <PurchaseOrders suppliers={suppliers} mats={allMats} />}

      {formOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(10,16,30,0.6)", zIndex: 60, display: "flex", justifyContent: "center", alignItems: "flex-start", overflowY: "auto", padding: "30px 12px" }}
          onClick={() => setFormOpen(false)} data-testid="supplier-form-overlay">
          <div className="bl-card" style={{ width: 720, maxWidth: "100%" }} onClick={(e) => e.stopPropagation()} data-testid="supplier-form">
            <div className="bl-eyebrow mb-3">{editId ? "تعديل مورّد" : "إضافة مورّد جديد"}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="bl-label">الاسم *</label>
                <input className="bl-input w-full" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
              <div><label className="bl-label">تليفون</label>
                <input className="bl-input w-full" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="supplier-phone-input" /></div>
              <div><label className="bl-label">واتساب</label>
                <input className="bl-input w-full" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="supplier-whatsapp-input" /></div>
              <div><label className="bl-label">إيميل</label>
                <input className="bl-input w-full" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-email-input" /></div>
              <div><label className="bl-label">موقع إلكتروني</label>
                <input className="bl-input w-full" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="supplier-website-input" /></div>
              <div><label className="bl-label">الدولة <button className="bl-pill" style={{ cursor: "pointer" }} onClick={() => addOption("country")} data-testid="add-country-option">+</button></label>
                <select className="bl-input w-full" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="supplier-country-select">
                  <option value="">— اختر —</option>
                  {options.country.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="bl-label">العملة <button className="bl-pill" style={{ cursor: "pointer" }} onClick={() => addOption("currency")} data-testid="add-currency-option">+</button></label>
                <select className="bl-input w-full" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="supplier-currency-select">
                  {options.currency.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="bl-label">شروط الدفع</label>
                <input className="bl-input w-full" value={form.paymentTerms} onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })} placeholder="50% مقدّم · الباقي عند التسليم" data-testid="supplier-terms-input" /></div>
              <div><label className="bl-label">MOQ</label>
                <input className="bl-input w-full" value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} placeholder="5 كجم" data-testid="supplier-moq-input" /></div>
              <div><label className="bl-label">مدة التوريد (أيام)</label>
                <input type="number" className="bl-input w-full" value={form.leadTimeDays} onChange={(e) => setForm({ ...form, leadTimeDays: e.target.value })} data-testid="supplier-leadtime-input" /></div>
              <div><label className="bl-label">التقييم (1–5)</label>
                <input type="number" min={1} max={5} className="bl-input w-full" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="supplier-rating-input" /></div>
              <div><label className="bl-label">الحالة</label>
                <select className="bl-input w-full" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="supplier-status-select">
                  <option value="active">نشِط</option><option value="paused">موقوف</option><option value="archived">مؤرشف</option>
                </select></div>
            </div>

            <div className="mt-3"><label className="bl-label">نوع التوريد (متعدد) <button className="bl-pill" style={{ cursor: "pointer" }} onClick={() => addOption("supplyTypes")} data-testid="add-supplytype-option">+</button></label>
              <div className="flex gap-1.5 flex-wrap mt-1" data-testid="supplier-supplytypes">
                {options.supplyTypes.map((t) => {
                  const on = form.supplyTypes.includes(t);
                  return (
                    <button key={t} className="bl-pill" data-testid={`supplytype-${t}`}
                      style={{ cursor: "pointer", background: on ? "rgba(201,169,97,0.3)" : undefined, border: on ? "1px solid rgba(201,169,97,0.7)" : undefined }}
                      onClick={() => setForm({ ...form, supplyTypes: on ? form.supplyTypes.filter((x) => x !== t) : [...form.supplyTypes, t] })}>
                      {on ? "✓ " : ""}{t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-3"><label className="bl-label">ملاحظات</label>
              <textarea className="bl-input w-full" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="supplier-notes-input" /></div>

            <div className="mt-4 bl-card" style={{ background: "rgba(201,169,97,0.06)" }} data-testid="ifra-section">
              <div className="bl-eyebrow mb-2">🛡 شهادة IFRA (شرط دخول السعودية)</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div><label className="bl-label">ملف الشهادة (PDF)</label>
                  <input type="file" accept=".pdf,image/*" className="bl-input w-full" onChange={(e) => setIfraFile(e.target.files?.[0] || null)} data-testid="ifra-file-input" /></div>
                <div><label className="bl-label">تاريخ الانتهاء</label>
                  <input type="date" className="bl-input w-full" value={ifraExpiry} onChange={(e) => setIfraExpiry(e.target.value)} data-testid="ifra-expiry-input" /></div>
                <div>
                  {editId && suppliers.find((s) => s.id === editId)?.ifra?.fileId && (
                    <a className="bl-btn" href={`${API}/suppliers/${editId}/ifra/file`} target="_blank" rel="noreferrer" data-testid="ifra-download-link">⬇ تحميل الشهادة الحالية</a>
                  )}
                </div>
              </div>
              {editId && (
                <div className="bl-sub mt-2">الحالة الحالية: <b style={{ color: ifraTone(suppliers.find((s) => s.id === editId)?.ifra?.status || "").fg }}>{suppliers.find((s) => s.id === editId)?.ifra?.status}</b></div>
              )}
            </div>

            {editId && (
              <div className="mt-4" data-testid="linked-materials-section">
                <div className="bl-eyebrow mb-2">🧪 الخامات المرتبطة (FK) · {linkedMats.length}</div>
                <div className="flex gap-2 items-center mb-2">
                  <select className="bl-input flex-1" value={matToLink} onChange={(e) => setMatToLink(e.target.value)} data-testid="link-material-select">
                    <option value="">— اختر خامة لربطها —</option>
                    {allMats.filter((m) => !linkedMats.some((l) => l.id === m.id)).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <button className="bl-btn" onClick={linkMaterial} disabled={!matToLink} data-testid="link-material-btn">اربط</button>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {linkedMats.map((m) => (
                    <span key={m.id} className="bl-pill" data-testid={`linked-mat-${m.id}`}>
                      {m.name} <button style={{ cursor: "pointer", color: "#e05656" }} onClick={() => unlinkMaterial(m.id)} data-testid={`unlink-mat-${m.id}`}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 flex gap-2 justify-between">
              <div>{editId && <button className="bl-btn" style={{ background: "rgba(220,70,70,0.15)", color: "#e05656" }} onClick={() => remove(editId)} data-testid="supplier-delete-btn">🗑 حذف المورّد</button>}</div>
              <div className="flex gap-2">
                <button className="bl-btn" onClick={() => setFormOpen(false)} data-testid="supplier-cancel-btn">إلغاء</button>
                <button className="bl-btn primary" onClick={save} disabled={busy} data-testid="supplier-save-btn">{busy ? "جارٍ الحفظ…" : "حفظ"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Purchase Orders + Receiving (multi-type lines) ──────────── */
const LINE_TYPES = ["زيت/خامة عطرية", "كحول", "زجاجات", "أغطية", "رشاشات/مضخّات", "علب/تغليف", "طباعة", "تلوين", "ليبلات", "أخرى"];
const KIND_BY_TYPE: Record<string, "material" | "component" | "service"> = {
  "زيت/خامة عطرية": "material", "كحول": "material",
  "زجاجات": "component", "أغطية": "component", "رشاشات/مضخّات": "component", "علب/تغليف": "component", "ليبلات": "component",
  "طباعة": "service", "تلوين": "service", "أخرى": "service",
};
type Comp = { id: string; name: string; unit: string; stockOnHand: number; avgCost?: number | null; currency?: string; specs?: string };
type Line = { lineType: string; rmId: string; componentId: string; itemName: string; qty: string; unitCost: string; specs: string };
const EMPTY_LINE: Line = { lineType: "زيت/خامة عطرية", rmId: "", componentId: "", itemName: "", qty: "", unitCost: "", specs: "" };

function PurchaseOrders({ suppliers, mats }: { suppliers: Supplier[]; mats: Mat[] }) {
  const [pos, setPos] = useState<PO[]>([]);
  const [comps, setComps] = useState<Comp[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [poSupplier, setPoSupplier] = useState("");
  const [poCurrency, setPoCurrency] = useState("EGP");
  const [poNotes, setPoNotes] = useState("");
  const [poLines, setPoLines] = useState<Line[]>([{ ...EMPTY_LINE }]);
  const [receiveFor, setReceiveFor] = useState<PO | null>(null);
  const [recLines, setRecLines] = useState<Record<number, { qty: string; unitCost: string }>>({});
  const [receipts, setReceipts] = useState<Record<string, any[]>>({});
  const [emailPoId, setEmailPoId] = useState<string | null>(null);
  const [emailPoTo, setEmailPoTo] = useState("");
  const [emailPoMsg, setEmailPoMsg] = useState<string | null>(null);
  const [lastMoves, setLastMoves] = useState<any[] | null>(null);

  const load = () => Promise.all([
    j(`/suppliers/purchase-orders/list`).then(setPos),
    j(`/suppliers/components`).then(setComps),
  ]).catch((e) => toast.error(String(e.message)));
  useEffect(() => { load(); }, []);

  const setLine = (i: number, patch: Partial<Line>) => setPoLines(poLines.map((x, xi) => xi === i ? { ...x, ...patch } : x));

  const quickAddComponent = async (i: number) => {
    const name = window.prompt("اسم الصنف الجديد (زجاجة 100مل، غطا ذهبي، علبة…):");
    if (!name) return;
    const specs = window.prompt("مواصفات (اختياري — مقاس/لون):") || undefined;
    const c = await j(`/suppliers/components`, { method: "POST", body: JSON.stringify({ name, specs, supplierId: poSupplier || undefined }) });
    setComps((prev) => prev.some((x) => x.id === c.id) ? prev : [...prev, c]);
    setLine(i, { componentId: c.id });
    toast.success("اتضاف لكتالوج المكوّنات");
  };

  const lineTotal = (l: Line) => {
    const t = Number(l.qty) * Number(l.unitCost);
    return isFinite(t) && l.qty && l.unitCost ? t.toFixed(2) : "—";
  };
  const grand = poLines.reduce((s, l) => {
    const t = Number(l.qty) * Number(l.unitCost);
    return s + (isFinite(t) && l.qty && l.unitCost ? t : 0);
  }, 0);

  const createPO = async () => {
    try {
      const lines = poLines
        .filter((l) => Number(l.qty) > 0 && (KIND_BY_TYPE[l.lineType] === "material" ? l.rmId : KIND_BY_TYPE[l.lineType] === "component" ? l.componentId : l.itemName))
        .map((l) => ({
          lineType: l.lineType, itemKind: KIND_BY_TYPE[l.lineType],
          rmId: l.rmId || undefined, componentId: l.componentId || undefined,
          itemName: l.itemName || undefined, qty: Number(l.qty),
          unitCost: l.unitCost ? Number(l.unitCost) : null, specs: l.specs || undefined,
        }));
      await j(`/suppliers/purchase-orders`, { method: "POST", body: JSON.stringify({ supplierId: poSupplier, currency: poCurrency, notes: poNotes || undefined, lines }) });
      toast.success("أمر الشراء اتعمل (مسودّة) — المخزون لم يتغيّر");
      setCreateOpen(false); setPoLines([{ ...EMPTY_LINE }]); setPoNotes("");
      load();
    } catch (e: any) { toast.error(String(e.message)); }
  };

  const setStatus = async (po: PO, to: string) => {
    try {
      await j(`/suppliers/purchase-orders/${po.id}/status`, { method: "POST", body: JSON.stringify({ to }) });
      toast.success(to === "sent" ? "اتبعت للمورّد — المخزون لم يتغيّر" : "تم الإلغاء");
      load();
    } catch (e: any) { toast.error(String(e.message)); }
  };

  const openReceive = (po: PO) => {
    setReceiveFor(po);
    const init: Record<number, { qty: string; unitCost: string }> = {};
    (po.lines as any[]).forEach((l, i) => { init[i] = { qty: "", unitCost: l.unitCost != null ? String(l.unitCost) : "" }; });
    setRecLines(init);
  };

  const doReceive = async () => {
    if (!receiveFor) return;
    try {
      const lines = Object.entries(recLines)
        .filter(([, v]) => Number(v.qty) > 0)
        .map(([idx, v]) => ({ lineIndex: Number(idx), qty: Number(v.qty), unitCost: v.unitCost ? Number(v.unitCost) : null }));
      const res = await j(`/suppliers/purchase-orders/${receiveFor.id}/receive`, {
        method: "POST", body: JSON.stringify({ lines, currency: receiveFor.currency }),
      });
      setLastMoves(res.stockMoves);
      toast.success(`تم الاستلام (${res.poStatus === "received" ? "اكتمل الأمر" : "استلام جزئي"})`);
      setReceiveFor(null);
      load();
    } catch (e: any) { toast.error(String(e.message)); }
  };

  const loadReceipts = async (po: PO) => {
    const r = await j(`/suppliers/purchase-orders/${po.id}/receipts`);
    setReceipts((prev) => ({ ...prev, [po.id]: r }));
  };

  return (
    <div className="space-y-3" data-testid="po-section">
      <div className="bl-sub">⚠️ القاعدة: <b>أمر الشراء لا يرفع المخزون</b> — الأصناف المخزونية (خامات/زجاجات/أغطية/علب/ليبلات) ترفع الرصيد عند <b>الاستلام الفعلي</b> فقط بوحداتها، والخدمات (طباعة/تلوين) تتسجّل كتكلفة بس.</div>
      <button className="bl-btn primary" onClick={() => setCreateOpen(true)} data-testid="po-create-btn">+ أمر شراء جديد</button>

      {lastMoves && lastMoves.length > 0 && (
        <div className="bl-card" style={{ borderColor: "rgba(46,160,90,0.5)" }} data-testid="stock-moves-proof">
          <div className="bl-eyebrow" style={{ color: "#2ea05a" }}>✅ حركة المخزون من الاستلام</div>
          {lastMoves.map((m, i) => (
            <div key={i} className="bl-sub mt-1">{m.item}: {m.stockBefore} ← <b>{m.stockAfter}</b> {m.unit}</div>
          ))}
        </div>
      )}

      <div className="bl-card">
        <table className="bl-table" data-testid="po-table">
          <thead><tr><th>رقم الأمر</th><th>المورّد</th><th>البنود</th><th>الإجمالي</th><th>الحالة</th><th>إجراءات</th></tr></thead>
          <tbody>
            {pos.map((po) => (
              <tr key={po.id} data-testid={`po-row-${po.poNumber}`}>
                <td><b>{po.poNumber}</b></td>
                <td>{po.supplierName}</td>
                <td>
                  {(po.lines as any[]).map((l, i) => (
                    <div key={i} className="bl-sub" style={{ fontSize: 10 }}>
                      [{l.lineType || "خامة"}] {l.itemName} · {l.qty ?? l.qtyG} {l.unit || "جم"} · مستلَم {l.receivedQty ?? l.receivedG ?? 0}
                      {l.itemKind === "service" ? " · خدمة (تكلفة فقط)" : ""}
                    </div>
                  ))}
                </td>
                <td>{(po as any).grandTotal != null ? `${(po as any).grandTotal} ${po.currency}` : "—"}</td>
                <td><span className="bl-pill" data-testid={`po-status-${po.poNumber}`}>{STATUS_AR[po.status] || po.status}</span></td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    <a className="bl-btn" href={`${API}/suppliers/purchase-orders/${po.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`po-pdf-${po.poNumber}`}>⬇ PDF</a>
                    <button className="bl-btn" data-testid={`po-email-${po.poNumber}`} onClick={() => {
                      setEmailPoId(emailPoId === po.id ? null : po.id);
                      setEmailPoTo(suppliers.find((s) => s.id === po.supplierId)?.email || "");
                      setEmailPoMsg(null);
                    }}>✉️ إيميل</button>
                    {po.status === "draft" && <button className="bl-btn" onClick={() => setStatus(po, "sent")} data-testid={`po-send-${po.poNumber}`}>إرسال للمورّد</button>}
                    {(po.status === "sent" || po.status === "partially_received") && <button className="bl-btn primary" onClick={() => openReceive(po)} data-testid={`po-receive-${po.poNumber}`}>📦 استلام</button>}
                    {(po.status === "draft" || po.status === "sent") && <button className="bl-btn" style={{ color: "#e05656" }} onClick={() => setStatus(po, "cancelled")} data-testid={`po-cancel-${po.poNumber}`}>إلغاء</button>}
                    <button className="bl-btn" onClick={() => loadReceipts(po)} data-testid={`po-receipts-${po.poNumber}`}>الاستلامات</button>
                  </div>
                  {emailPoId === po.id && (
                    <div className="flex gap-1 mt-1 items-center" data-testid={`po-email-form-${po.poNumber}`}>
                      <input className="bl-input" style={{ width: 190, fontSize: 11 }} placeholder="بريد المورّد" value={emailPoTo}
                        onChange={(e) => setEmailPoTo(e.target.value)} data-testid={`po-email-to-${po.poNumber}`} />
                      <button className="bl-btn primary" data-testid={`po-email-send-${po.poNumber}`} onClick={async () => {
                        setEmailPoMsg("...");
                        try {
                          const r = await fetch(`${API}/email/send-document`, {
                            method: "POST", credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ docKey: "po", recordId: po.id, to: emailPoTo.trim(), subject: `أمر شراء ${po.poNumber} — ORVAN` }),
                          });
                          const j = await r.json().catch(() => ({}));
                          if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
                          setEmailPoMsg(`اتبعت ✓`); setEmailPoId(null);
                        } catch (e) { setEmailPoMsg(`فشل: ${(e as Error).message}`); }
                      }}>إرسال</button>
                    </div>
                  )}
                  {emailPoMsg && emailPoMsg !== "..." && <div className="bl-sub mt-1" style={{ fontSize: 10 }} data-testid={`po-email-msg-${po.poNumber}`}>{emailPoMsg}</div>}
                  {(receipts[po.id] || []).map((r) => (
                    <div key={r.id} className="bl-sub mt-1" style={{ fontSize: 10 }} data-testid={`receipt-${r.id}`}>
                      🧾 {String(r.receivedAt).slice(0, 10)} · {r.lines.map((l: any) => `${l.itemName} ${l.qty}${l.unit || ""} @${l.unitCost ?? "—"}${l.serviceCostOnly ? " (خدمة)" : ""}`).join(" · ")} · إجمالي {r.totalCost ?? "—"}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
            {pos.length === 0 && <tr><td colSpan={6} className="bl-sub text-center py-5" data-testid="po-empty">لا توجد أوامر شراء</td></tr>}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(10,16,30,0.6)", zIndex: 60, display: "flex", justifyContent: "center", alignItems: "flex-start", overflowY: "auto", padding: "30px 12px" }} onClick={() => setCreateOpen(false)}>
          <div className="bl-card" style={{ width: 880, maxWidth: "100%" }} onClick={(e) => e.stopPropagation()} data-testid="po-create-form">
            <div className="bl-eyebrow mb-3">أمر شراء جديد (مسودّة — لا يرفع مخزون)</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="bl-label">المورّد *</label>
                <select className="bl-input w-full" value={poSupplier} onChange={(e) => setPoSupplier(e.target.value)} data-testid="po-supplier-select">
                  <option value="">— اختر —</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></div>
              <div><label className="bl-label">العملة</label>
                <select className="bl-input w-full" value={poCurrency} onChange={(e) => setPoCurrency(e.target.value)} data-testid="po-currency-select">
                  {["EGP", "SAR", "USD", "EUR", "CNY", "AED"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
            </div>
            <div className="mt-3">
              <label className="bl-label">البنود</label>
              {poLines.map((l, i) => {
                const kind = KIND_BY_TYPE[l.lineType];
                return (
                  <div key={i} className="bl-card mt-2" style={{ padding: 10 }}>
                    <div className="flex gap-2 flex-wrap items-end">
                      <div><label className="bl-label" style={{ fontSize: 9 }}>نوع البند</label>
                        <select className="bl-input" style={{ width: 150 }} value={l.lineType} onChange={(e) => setLine(i, { lineType: e.target.value, rmId: "", componentId: "", itemName: "" })} data-testid={`po-line-type-${i}`}>
                          {LINE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select></div>
                      {kind === "material" && (
                        <div className="flex-1" style={{ minWidth: 180 }}><label className="bl-label" style={{ fontSize: 9 }}>الخامة (من المكتبة) · جم</label>
                          <select className="bl-input w-full" value={l.rmId} onChange={(e) => setLine(i, { rmId: e.target.value })} data-testid={`po-line-mat-${i}`}>
                            <option value="">— خامة —</option>
                            {mats.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                          </select></div>
                      )}
                      {kind === "component" && (
                        <div className="flex-1 flex gap-1 items-end" style={{ minWidth: 200 }}>
                          <div className="flex-1"><label className="bl-label" style={{ fontSize: 9 }}>الصنف (كتالوج المكوّنات) · قطعة</label>
                            <select className="bl-input w-full" value={l.componentId} onChange={(e) => setLine(i, { componentId: e.target.value })} data-testid={`po-line-comp-${i}`}>
                              <option value="">— صنف —</option>
                              {comps.map((cp) => <option key={cp.id} value={cp.id}>{cp.name}{cp.specs ? ` (${cp.specs})` : ""}</option>)}
                            </select></div>
                          <button className="bl-pill" style={{ cursor: "pointer" }} onClick={() => quickAddComponent(i)} data-testid={`po-line-addcomp-${i}`}>+ صنف</button>
                        </div>
                      )}
                      {kind === "service" && (
                        <div className="flex-1" style={{ minWidth: 180 }}><label className="bl-label" style={{ fontSize: 9 }}>الخدمة (تكلفة فقط — لا مخزون)</label>
                          <input className="bl-input w-full" placeholder="طباعة علب · تلوين زجاج…" value={l.itemName} onChange={(e) => setLine(i, { itemName: e.target.value })} data-testid={`po-line-service-${i}`} /></div>
                      )}
                      <div><label className="bl-label" style={{ fontSize: 9 }}>الكمية ({kind === "material" ? "جم" : "قطعة"})</label>
                        <input type="number" className="bl-input" style={{ width: 95 }} value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`po-line-qty-${i}`} /></div>
                      <div><label className="bl-label" style={{ fontSize: 9 }}>سعر الوحدة</label>
                        <input type="number" className="bl-input" style={{ width: 95 }} value={l.unitCost} onChange={(e) => setLine(i, { unitCost: e.target.value })} data-testid={`po-line-cost-${i}`} /></div>
                      <div><label className="bl-label" style={{ fontSize: 9 }}>الإجمالي</label>
                        <div className="bl-pill" data-testid={`po-line-total-${i}`}>{lineTotal(l)}</div></div>
                      <button className="bl-pill" style={{ cursor: "pointer", color: "#e05656" }} onClick={() => setPoLines(poLines.filter((_, xi) => xi !== i))}>×</button>
                    </div>
                    <input className="bl-input w-full mt-2" placeholder="تفاصيل/مواصفات (مقاس · لون · مواصفة)" value={l.specs} onChange={(e) => setLine(i, { specs: e.target.value })} data-testid={`po-line-specs-${i}`} />
                  </div>
                );
              })}
              <div className="flex justify-between items-center mt-2">
                <button className="bl-pill" style={{ cursor: "pointer" }} onClick={() => setPoLines([...poLines, { ...EMPTY_LINE }])} data-testid="po-add-line-btn">+ بند</button>
                <div className="bl-eyebrow" data-testid="po-grand-total">الإجمالي الكلي: {grand.toFixed(2)} {poCurrency}</div>
              </div>
            </div>
            <div className="mt-2"><label className="bl-label">ملاحظات</label>
              <input className="bl-input w-full" value={poNotes} onChange={(e) => setPoNotes(e.target.value)} data-testid="po-notes-input" /></div>
            <div className="mt-4 flex gap-2 justify-end">
              <button className="bl-btn" onClick={() => setCreateOpen(false)}>إلغاء</button>
              <button className="bl-btn primary" onClick={createPO} disabled={!poSupplier} data-testid="po-save-btn">إنشاء الأمر</button>
            </div>
          </div>
        </div>
      )}

      {receiveFor && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(10,16,30,0.6)", zIndex: 60, display: "flex", justifyContent: "center", alignItems: "flex-start", overflowY: "auto", padding: "40px 12px" }} onClick={() => setReceiveFor(null)}>
          <div className="bl-card" style={{ width: 680, maxWidth: "100%" }} onClick={(e) => e.stopPropagation()} data-testid="po-receive-form">
            <div className="bl-eyebrow mb-1">📦 استلام فعلي · {receiveFor.poNumber}</div>
            <div className="bl-sub mb-3">سجّل الكمية اللي <b>وصلت فعلاً</b> والتكلفة الحقيقية وقت التوريد ({receiveFor.currency}) — الأصناف المخزونية هترفع الرصيد وتحدّث متوسط التكلفة · الخدمات هتتسجّل كتكلفة بس. الاستلام الجزئي مسموح.</div>
            {(receiveFor.lines as any[]).map((l, i) => {
              const remaining = Math.max(0, (l.qty ?? l.qtyG ?? 0) - (l.receivedQty ?? l.receivedG ?? 0));
              return (
                <div key={i} className="flex gap-2 items-center mt-1">
                  <div className="flex-1 bl-sub">[{l.lineType || "خامة"}] {l.itemName} <span style={{ fontSize: 10 }}>(متبقّي {remaining} {l.unit || "جم"}{l.itemKind === "service" ? " · خدمة" : ""})</span></div>
                  <input type="number" className="bl-input" style={{ width: 115 }} placeholder="كمية مستلمة"
                    value={recLines[i]?.qty || ""} onChange={(e) => setRecLines({ ...recLines, [i]: { ...recLines[i], qty: e.target.value } })} data-testid={`receive-qty-${i}`} />
                  <input type="number" className="bl-input" style={{ width: 115 }} placeholder="تكلفة فعلية"
                    value={recLines[i]?.unitCost || ""} onChange={(e) => setRecLines({ ...recLines, [i]: { ...recLines[i], unitCost: e.target.value } })} data-testid={`receive-cost-${i}`} />
                </div>
              );
            })}
            <div className="mt-4 flex gap-2 justify-end">
              <button className="bl-btn" onClick={() => setReceiveFor(null)}>إلغاء</button>
              <button className="bl-btn primary" onClick={doReceive} data-testid="receive-confirm-btn">تأكيد الاستلام</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
